/**
 * Iter85 — Biometric Ops (per-module 3-tab view).
 *
 *   /m/:moduleCode/biometric
 *     tab 1: Devices  — health table + status badges
 *     tab 2: Events   — filterable 7-day paginated event log
 *     tab 3: Exceptions — HR backfill table + modal
 *
 * RBAC
 *   - Super Admin / HR Manager / HR Officer    → full access, backfill allowed
 *   - Dept Manager (that module only)          → view-only
 *   - Attendance Officer                       → view-only
 *
 * Dates are rendered in Africa/Mogadishu local wall-clock; UTC originals
 * are available in tooltips.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { useAuth } from "../contexts/AuthContext";
import {
    Wifi, WifiOff, AlertCircle, LogIn, LogOut, RefreshCw,
    FileWarning, ChevronLeft, ChevronRight,
} from "lucide-react";


function fmtLocal(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Mogadishu",
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}


function StatusPill({ status }) {
    const map = {
        online:  { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: Wifi },
        stale:   { cls: "bg-amber-50 text-amber-700 ring-amber-200",       Icon: AlertCircle },
        offline: { cls: "bg-rose-50 text-rose-700 ring-rose-200",          Icon: WifiOff },
    };
    const cfg = map[status] || map.offline;
    const Icon = cfg.Icon;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 text-[11px] font-medium ${cfg.cls}`}
            data-testid={`iter85-device-status-${status}`}
        >
            <Icon className="w-3 h-3" />
            {status}
        </span>
    );
}

function RolePill({ role }) {
    if (role === "check_in")
        return (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200">
                <LogIn className="w-3 h-3" /> check-in
            </span>
        );
    if (role === "check_out")
        return (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                <LogOut className="w-3 h-3" /> check-out
            </span>
        );
    return <span className="text-[10px] text-slate-400">unassigned</span>;
}


// ────────────────────────────── Devices tab ──────────────────────────────

function DevicesTab({ moduleCode }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/m/${moduleCode}/biometric/devices`);
            setData(r.data);
            setErr(null);
        } catch (e) {
            setErr(formatApiError(e));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleCode]);

    if (loading) return <div className="p-6 text-slate-500">Loading devices…</div>;
    if (err) return <div className="p-6 text-rose-700">{err}</div>;

    const devices = data?.devices || [];

    return (
        <div className="space-y-4" data-testid="iter85-devices-tab">
            <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                    {devices.length} device{devices.length !== 1 ? "s" : ""} in {moduleCode}
                </div>
                <Button size="sm" variant="outline" onClick={load} data-testid="iter85-devices-refresh">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                </Button>
            </div>
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Serial</th>
                            <th className="text-left px-3 py-2 font-medium">Device</th>
                            <th className="text-left px-3 py-2 font-medium">Role</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-left px-3 py-2 font-medium">Last Heartbeat</th>
                            <th className="text-right px-3 py-2 font-medium">Age (min)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {devices.length === 0 && (
                            <tr><td colSpan={6} className="p-6 text-center text-slate-400">No devices registered.</td></tr>
                        )}
                        {devices.map((d) => (
                            <tr key={d.device_id} className="border-t border-slate-100 hover:bg-slate-50/60"
                                data-testid={`iter85-device-row-${d.serial_number}`}>
                                <td className="px-3 py-2 font-mono text-xs">{d.serial_number || "—"}</td>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{d.device_name || "—"}</div>
                                    {d.device_model && <div className="text-xs text-slate-500">{d.device_model}</div>}
                                </td>
                                <td className="px-3 py-2"><RolePill role={d.device_role} /></td>
                                <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                                <td className="px-3 py-2 whitespace-nowrap"
                                    title={d.last_heartbeat_utc || "never"}>
                                    {fmtLocal(d.last_heartbeat_utc)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {d.minutes_since_heartbeat !== null ? d.minutes_since_heartbeat : "∞"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ────────────────────────────── Events tab ──────────────────────────────

function EventsTab({ moduleCode }) {
    const today = new Date();
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(today); d.setDate(d.getDate() - 6);
        return d.toISOString().slice(0, 10);
    });
    const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
    const [employeeId, setEmployeeId] = useState("");
    const [eventType, setEventType] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const load = async (p = page) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                date_from: dateFrom, date_to: dateTo, page: p, size: 50,
            });
            if (employeeId) params.set("employee_id", employeeId);
            if (eventType) params.set("event_type", eventType);
            const r = await api.get(`/m/${moduleCode}/biometric/events?${params}`);
            setData(r.data); setPage(p); setErr(null);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(1); /* eslint-disable-next-line */ }, [moduleCode]);

    const total = data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / 50));

    return (
        <div className="space-y-4" data-testid="iter85-events-tab">
            <div className="flex flex-wrap items-end gap-3 bg-slate-50 rounded-lg p-3 ring-1 ring-slate-200">
                <div>
                    <Label className="text-[11px] text-slate-500">From</Label>
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                           data-testid="iter85-events-date-from" />
                </div>
                <div>
                    <Label className="text-[11px] text-slate-500">To</Label>
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                           data-testid="iter85-events-date-to" />
                </div>
                <div>
                    <Label className="text-[11px] text-slate-500">Employee ID</Label>
                    <Input placeholder="(optional)" value={employeeId} onChange={e => setEmployeeId(e.target.value)}
                           data-testid="iter85-events-employee" />
                </div>
                <div>
                    <Label className="text-[11px] text-slate-500">Event type</Label>
                    <select
                        className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                        value={eventType} onChange={e => setEventType(e.target.value)}
                        data-testid="iter85-events-type">
                        <option value="">All</option>
                        <option value="clock_in">clock_in</option>
                        <option value="clock_out">clock_out</option>
                    </select>
                </div>
                <Button onClick={() => load(1)} disabled={loading} data-testid="iter85-events-apply">
                    {loading ? "Loading…" : "Apply filters"}
                </Button>
            </div>
            {err && <div className="text-rose-700 text-sm">{err}</div>}

            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Time (local)</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Type</th>
                            <th className="text-left px-3 py-2 font-medium">Source</th>
                            <th className="text-left px-3 py-2 font-medium">Device</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.events || []).length === 0 && !loading && (
                            <tr><td colSpan={5} className="p-6 text-center text-slate-400">No events.</td></tr>
                        )}
                        {(data?.events || []).map((e) => (
                            <tr key={e.id} className="border-t border-slate-100">
                                <td className="px-3 py-2 whitespace-nowrap" title={e.event_time}>
                                    {fmtLocal(e.event_time)}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{e.employee_code || e.employee_id?.slice(0, 8)}</td>
                                <td className="px-3 py-2">{e.event_type}</td>
                                <td className="px-3 py-2 text-slate-500">{e.source}</td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-500">{e.device_id?.slice(0, 8) || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-sm">
                <div className="text-slate-500">{total.toLocaleString()} events</div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1 || loading}
                            onClick={() => load(page - 1)} data-testid="iter85-events-prev">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="tabular-nums">Page {page} / {pages}</span>
                    <Button variant="outline" size="sm" disabled={page >= pages || loading}
                            onClick={() => load(page + 1)} data-testid="iter85-events-next">
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}


// ─────────────────────────── Exceptions tab ───────────────────────────

function ExceptionsTab({ moduleCode }) {
    const { user } = useAuth();
    const canBackfill = useMemo(() =>
        [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR].includes(user?.role),
    [user?.role]);
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [picking, setPicking] = useState(null); // row being backfilled

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/m/${moduleCode}/biometric/exceptions?date=${date}`);
            setRows(r.data.exceptions || []);
            setErr(null);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleCode, date]);

    return (
        <div className="space-y-4" data-testid="iter85-exceptions-tab">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <Label className="text-[11px] text-slate-500">Date</Label>
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                           data-testid="iter85-exceptions-date" />
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="iter85-exceptions-refresh">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                </Button>
                {!canBackfill && (
                    <div className="text-xs text-slate-500 ml-auto bg-amber-50 ring-1 ring-amber-200 rounded px-2 py-1">
                        View-only. Only HR Manager or Super Admin may submit a backfill.
                    </div>
                )}
            </div>
            {err && <div className="text-rose-700 text-sm">{err}</div>}

            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Missing</th>
                            <th className="text-left px-3 py-2 font-medium">Raw other side (local)</th>
                            <th className="text-left px-3 py-2 font-medium">Suggested time</th>
                            <th className="text-right px-3 py-2 font-medium">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={5} className="p-6 text-center text-slate-400">
                                No exceptions. Everyone has both clock-in and clock-out for {date}.
                            </td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.employee_id} className="border-t border-slate-100"
                                data-testid={`iter85-exception-row-${r.employee_id}`}>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{r.employee_name || r.employee_id.slice(0, 8)}</div>
                                    <div className="text-xs text-slate-500 font-mono">{r.employee_code || r.staff_id || r.employee_id}</div>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 text-[11px] font-medium ${
                                        r.missing === "check_in"
                                            ? "bg-sky-50 text-sky-700 ring-sky-200"
                                            : "bg-indigo-50 text-indigo-700 ring-indigo-200"}`}>
                                        <FileWarning className="w-3 h-3" />
                                        {r.missing}
                                    </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{fmtLocal(r.raw_other_side_utc)}</td>
                                <td className="px-3 py-2 font-mono">{r.suggested_time || "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    {canBackfill ? (
                                        <Button size="sm"
                                                disabled={r.period_locked}
                                                onClick={() => setPicking(r)}
                                                data-testid={`iter85-backfill-btn-${r.employee_id}`}>
                                            {r.period_locked ? "Period locked" : "Backfill"}
                                        </Button>
                                    ) : (
                                        <span className="text-xs text-slate-400">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <BackfillDialog
                row={picking}
                moduleCode={moduleCode}
                onClose={() => setPicking(null)}
                onDone={() => { setPicking(null); load(); }}
            />
        </div>
    );
}


function BackfillDialog({ row, moduleCode, onClose, onDone }) {
    const [time, setTime] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (row) {
            setTime(row.suggested_time || "08:00");
            setReason("");
        }
    }, [row]);

    const submit = async () => {
        if (!row) return;
        if (!time.match(/^\d{2}:\d{2}$/)) {
            toast.error("Time must be HH:MM (24h)"); return;
        }
        if (reason.trim().length < 3) {
            toast.error("Reason must be at least 3 characters"); return;
        }
        setSaving(true);
        try {
            const r = await api.post(
                `/attendance/${moduleCode}/biometric-exception-backfill`,
                {
                    employee_id: row.employee_id,
                    local_date: row.local_date,
                    event_type: row.missing,
                    local_time: time,
                    reason,
                },
            );
            toast.success(`Backfilled ${row.missing} at ${time} for ${row.employee_name || row.employee_id.slice(0, 8)}`);
            onDone(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
            <DialogContent data-testid="iter85-backfill-dialog">
                <DialogHeader>
                    <DialogTitle>Backfill {row?.missing}</DialogTitle>
                    <DialogDescription>
                        {row?.employee_name || row?.employee_id?.slice(0, 8)} — {row?.local_date}
                        <br />Raw other side: <span className="font-mono">{fmtLocal(row?.raw_other_side_utc)}</span>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Local time (HH:MM)</Label>
                        <Input type="time" value={time} onChange={e => setTime(e.target.value)}
                               data-testid="iter85-backfill-time" />
                    </div>
                    <div>
                        <Label>Reason</Label>
                        <textarea
                            className="w-full min-h-[80px] rounded-md border border-slate-300 p-2 text-sm"
                            placeholder="e.g. Device offline between 06:00–09:00 — supervisor confirmed presence"
                            value={reason} onChange={e => setReason(e.target.value)}
                            data-testid="iter85-backfill-reason" />
                        <div className="text-[11px] text-slate-500 mt-1">
                            Audit-logged under <code>iter85_biometric_backfill</code>.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="iter85-backfill-submit">
                        {saving ? "Submitting…" : "Submit backfill"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────── Page shell ───────────────────────────

export default function BiometricOps() {
    const { moduleCode } = useParams();
    const code = (moduleCode || "").toUpperCase();
    const [tab, setTab] = useState("devices");

    return (
        <div className="space-y-6" data-testid="iter85-biometric-ops-page">
            <div>
                <h1 className="text-2xl font-semibold">Biometric — {code}</h1>
                <p className="text-sm text-slate-500">
                    Device health, raw event log, and HR exception backfill for the {code} module.
                </p>
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="devices" data-testid="iter85-tab-devices">Devices</TabsTrigger>
                    <TabsTrigger value="events" data-testid="iter85-tab-events">Events</TabsTrigger>
                    <TabsTrigger value="exceptions" data-testid="iter85-tab-exceptions">Exceptions</TabsTrigger>
                </TabsList>
                <TabsContent value="devices" className="pt-4"><DevicesTab moduleCode={code} /></TabsContent>
                <TabsContent value="events" className="pt-4"><EventsTab moduleCode={code} /></TabsContent>
                <TabsContent value="exceptions" className="pt-4"><ExceptionsTab moduleCode={code} /></TabsContent>
            </Tabs>
        </div>
    );
}
